import { useRouter } from "@tanstack/react-router"
import dayjs from "dayjs"
import { MessageNotif, Truck } from "iconsax-reactjs"
import { AlertTriangle, Check, ChevronRight, Copy } from "lucide-react"
import React, { useState } from "react"
import { GoVerified } from "react-icons/go"

import { IconButton, TabTag } from "@/components/shared"
import { Button, Image, Skeleton } from "@/components/ui"
import ComplaintModal from "@/features/jobs/manage/components/complaint-modal"
import { OrderTrackingSheet } from "@/features/jobs/manage/components/order-tracking-sheet"
import {
  ORDER_STATUS_MAP,
  translateTitle
} from "@/features/jobs/manage/constants/order-tracking.utils"
import { AFFILIATE_CAMPAIGN_TYPE_DELIVERY_CONTENT } from "@/modules/helpers/common"
import {
  ECampaignType,
  EFolderAssets,
  EReasonRejectAcceptance,
  ESubmitStatus
} from "@/modules/helpers/enum"
import { useCopyToClipboard } from "@/modules/helpers/hooks"
import { useShowSubmission } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { getImageByNameFromS3, sendHaptic } from "@/modules/helpers/utils"
import type {
  AffiliateCampaign,
  Campaign,
  SubmissionCampaign,
  SubmissionOrder
} from "@/modules/types"

// ============================================================================
// Constants
// ============================================================================

const CSS_COLORS: Record<string, string> = {
  red: "#ff0000",
  blue: "#0000ff",
  green: "#008000",
  orange: "#ffa500",
  black: "#000000",
  white: "#ffffff"
}

// const DEADLINE_DAYS_IN_APP = 7
// const DEADLINE_DAYS_OUT_APP = 14

// ============================================================================
// Utilities
// ============================================================================

function parseColor(color: string | undefined, alpha = 0.08): string | undefined {
  if (!color) return undefined

  const hex = color.startsWith("#") ? color : CSS_COLORS[color.toLowerCase()]
  if (!hex) return undefined

  let clean = hex.replace("#", "")
  if (clean.length === 3) {
    clean = clean
      .split("")
      .map((c) => c + c)
      .join("")
  }

  const bigint = parseInt(clean, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// ============================================================================
// Sub-Components
// ============================================================================

interface StatusBadgesProps {
  isOverdue: boolean
  hasProtest: boolean
}

const StatusBadges: React.FC<StatusBadgesProps> = ({ isOverdue, hasProtest }) => {
  if (!isOverdue) return null

  return (
    <div className="flex flex-row items-center justify-end gap-2 py-1">
      <div className="flex flex-row items-center justify-end gap-2">
        {hasProtest && (
          <span className="bg-green-100 border-green-500 text-green-600 font-medium text-[10px] rounded-full h-6 px-2 flex items-center">
            Đang xử lý
          </span>
        )}
        <span className="h-6 rounded-full px-2 text-[10px] font-medium flex items-center gap-1 bg-red-500 text-white">
          <AlertTriangle className="h-3 w-3" />
          Quá hạn
        </span>
      </div>
    </div>
  )
}

interface ThumbnailProps {
  campaign?: Campaign | AffiliateCampaign
  folderAssets: EFolderAssets
  shopName?: string
  onChatClick: (e: React.MouseEvent) => void
}

const Thumbnail: React.FC<ThumbnailProps> = ({ campaign, folderAssets, shopName, onChatClick }) => {
  const imageSrc = campaign?.thumbnailName?.includes("https")
    ? campaign.thumbnailName
    : getImageByNameFromS3(campaign?.thumbnailName, campaign?.createdBy, folderAssets)

  return (
    <div className="w-40 rounded-2xl h-40 border border-gray-200 overflow-hidden relative">
      <Image
        src={imageSrc}
        alt=""
        className="w-full aspect-square rounded-lg shadow-sm group-hover:scale-110 object-cover transition-all duration-200 ease-linear"
      />
      <ShopBadge shopName={shopName} />
      <ChatButton onClick={onChatClick} />
    </div>
  )
}

interface ShopBadgeProps {
  shopName?: string
}

const ShopBadge: React.FC<ShopBadgeProps> = ({ shopName }) => (
  <div className="rounded-tr-[16px] rounded-bl-[10px] border-l-[0.5px] border-b-[0.5px] border-neutral-200 bg-gradient-primary absolute bottom-0 px-3 flex justify-center items-center gap-1 py-1.5 text-white">
    <GoVerified size={12} color="#fff" />
    <span className="text-xss font-light w-[80%] overflow-ellipsis line-clamp-1">
      {shopName || "Nhãn hàng"}
    </span>
  </div>
)

interface ChatButtonProps {
  onClick: (e: React.MouseEvent) => void
}

const ChatButton: React.FC<ChatButtonProps> = ({ onClick }) => (
  <div className="absolute top-2 right-2" onClick={onClick}>
    <div className="bg-white border border-primary p-2 rounded-full">
      <MessageNotif size={16} color="#045664" />
    </div>
  </div>
)

interface TagsProps {
  isSelfFaceRequired?: boolean
}

const Tags: React.FC<TagsProps> = ({ isSelfFaceRequired }) => (
  <div className="flex gap-1">
    <TabTag label="Mẫu miễn phí" className="text-xss" />
    {isSelfFaceRequired && <TabTag label="Cần show mặt" className="text-xss" />}
  </div>
)

interface CashAmountProps {
  amount?: number
}

const CashAmount: React.FC<CashAmountProps> = ({ amount }) => {
  if (!amount) return <div className="my-1" />

  return (
    <span className="text-gradient-primary text-sm font-semibold my-2">
      Cast: {Number(amount).toLocaleString()}đ /KOC
    </span>
  )
}

interface StatusSectionProps {
  submission?: SubmissionCampaign
  skuTitle?: string
  onComplaintClick: (e: React.MouseEvent) => void
  onOrderClick: (e: React.MouseEvent) => void
}

const StatusSection: React.FC<StatusSectionProps> = ({
  submission,
  skuTitle,
  onComplaintClick,
  onOrderClick
}) => {
  const isOverdue = submission?.status === ESubmitStatus.OVERDUE
  const isFinished = submission?.status === ESubmitStatus.FINISHED

  const hasNoProtest = !submission?.protestOverdues?.reason
  const order = submission?.ordersTap

  // If order exists, use order status
  // If order exists, use latest logistics status
  if (order && order?.ordersLogistics?.length && !isFinished) {
    // Get the latest logistics event
    const sortedLogistics = [...(order.ordersLogistics || [])].sort(
      (a, b) => Number(b.eventTime) - Number(a.eventTime)
    )
    const latestEvent = sortedLogistics[0]

    // Determine status label and color
    let statusLabel = order.status
    let statusColor = "text-gray-600"
    let statusBgColor = "bg-gray-100"

    if (latestEvent) {
      statusLabel = translateTitle(latestEvent.title)

      // Map the translated or original title to a color if possible, or fallback to default
      // We can check if the title matches any key in ORDER_STATUS_MAP
      const mappedStatus = ORDER_STATUS_MAP[latestEvent.title] || ORDER_STATUS_MAP[order.status]

      if (mappedStatus) {
        statusLabel = mappedStatus.label
        statusColor = mappedStatus.color
        statusBgColor = mappedStatus.bgColor
      }
    } else {
      const statusInfo = ORDER_STATUS_MAP[order.status]
      if (statusInfo) {
        statusLabel = statusInfo.label
        statusColor = statusInfo.color
        statusBgColor = statusInfo.bgColor
      }
    }

    return (
      <div
        onClick={onOrderClick}
        className={cn(
          "flex gap-1 items-center justify-between rounded-md p-2 w-full cursor-pointer",
          statusBgColor,
          statusColor
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Truck size={16} variant="Bold" className="shrink-0" />
          <span className="text-xs font-medium truncate">{statusLabel}</span>
        </div>

        {isOverdue && hasNoProtest ? (
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation()
              onComplaintClick(e)
            }}
            className="text-xs !bg-red-200 !border-red-500 !text-red-500 font-medium rounded-full h-6 px-2 text-xss ml-auto"
          >
            Khiếu nại
          </Button>
        ) : (
          <ChevronRight size={16} className="shrink-0 opacity-50" />
        )}
      </div>
    )
  }

  return (
    <div
      className={cn("flex gap-1 items-start justify-between rounded-md p-2 w-full", {
        "items-center": isOverdue
      })}
      style={{
        backgroundColor: submission?.metadataContent?.color
          ? parseColor(submission.metadataContent.color, 0.09)
          : undefined,
        color: submission?.metadataContent?.color
      }}
    >
      <div className="text-xs leading-relaxed">
        <span className="font-medium">{submission?.metadataContent?.status}</span>
        {skuTitle && <span className="font-normal"> - {skuTitle.replace(/\s*-\s*/g, ", ")}</span>}
      </div>
      {isOverdue && hasNoProtest && (
        <Button
          variant="outline"
          onClick={onComplaintClick}
          className="text-xs !bg-red-200 !border-red-500 !text-red-500 font-medium rounded-full h-6 px-2 text-xss ml-auto"
        >
          Khiếu nại
        </Button>
      )}
    </div>
  )
}

interface DeliveryInfoProps {
  submission?: SubmissionCampaign
  copied: boolean
  onCopy: () => void
}

const DeliveryInfo: React.FC<DeliveryInfoProps> = ({ submission, copied, onCopy }) => {
  const requestSamples = submission?.requestProductSamples
  const hasTrackingOrDelivery =
    !!requestSamples?.trackingNumber || !!requestSamples?.deliveryService

  if (!hasTrackingOrDelivery) return null

  const campaignType = submission?.affCampaigns
    ?.type as keyof typeof AFFILIATE_CAMPAIGN_TYPE_DELIVERY_CONTENT
  const deliveryContent = AFFILIATE_CAMPAIGN_TYPE_DELIVERY_CONTENT[campaignType]

  return (
    <div className="flex flex-col gap-2 mt-2">
      {deliveryContent && <InfoRow label="Loại chiến dịch:" value={deliveryContent} />}
      {requestSamples?.trackingNumber && (
        <div className="flex gap-2 items-center">
          <span className="text-xs text-black font-semibold">Mã vận đơn: </span>
          <div className="flex items-center gap-1">
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                onCopy()
              }}
              mode="ghost"
              className="w-5 h-5 p-0"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </IconButton>
            <span className="text-xs font-normal">{requestSamples.trackingNumber}</span>
          </div>
        </div>
      )}
      {requestSamples?.deliveryService && (
        <InfoRow label="Đơn vị vận chuyển:" value={requestSamples.deliveryService} />
      )}
    </div>
  )
}

interface InfoRowProps {
  label: string
  value: string
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div className="flex gap-2 items-center">
    <span className="text-xs text-black font-semibold">{label}</span>
    <span className="text-xs font-normal">{value}</span>
  </div>
)

interface DraftDueDatesProps {
  campaign?: Campaign
  campaignType: ECampaignType
}

const DraftDueDates: React.FC<DraftDueDatesProps> = ({ campaign, campaignType }) => {
  if (campaignType !== ECampaignType.REVIEW_PRODUCT) return null

  const draftDates = (campaign as Campaign)?.draftDueDates

  return (
    <div className="text-xs text-black font-semibold flex flex-col justify-center">
      <p>Hạn bản nháp:</p>
      <div className="flex flex-col gap-1 py-2">
        {draftDates?.map((date, index) => (
          <div key={index} className="flex items-center gap-1 justify-start">
            <p className="text-xs font-medium border border-primary px-2 py-1 flex rounded-lg items-center gap-1 bg-primary text-white">
              Bản nháp: {index + 1}
            </p>
            <p className="text-xs font-medium">Ngày: {date && dayjs(date).format("DD/MM/YYYY")}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

interface RejectReasonProps {
  reason?: string
}

const RejectReason: React.FC<RejectReasonProps> = ({ reason }) => {
  if (!reason) return null

  return (
    <div className="bg-gray-100 p-2 rounded-lg">
      <span className="text-xs font-semibold">Lý do: </span>
      <span className="text-xs font-normal">{reason}</span>
    </div>
  )
}

interface DeadlineInfoProps {
  type?: string
  workingAt?: string
  status?: ESubmitStatus
  order?: SubmissionOrder
  expiresAt?: string
}

const DeadlineInfo: React.FC<DeadlineInfoProps> = ({
  type,
  workingAt,
  status,
  order,
  expiresAt
}) => {
  if (!workingAt || status === ESubmitStatus.FINISHED) return null

  // If order exists, only show deadline if delivered (check latest logistics)
  if (order && order?.ordersLogistics?.length) {
    const sortedLogistics = [...(order.ordersLogistics || [])].sort(
      (a, b) => Number(b.eventTime) - Number(a.eventTime)
    )
    const latestEvent = sortedLogistics[0]
    const isDelivered = latestEvent
      ? latestEvent.title === "Delivered"
      : order.status === "DELIVERED"

    if (!isDelivered) return null
  }

  // let days = DEADLINE_DAYS_OUT_APP

  // if (
  //   order &&
  //   order?.ordersLogistics?.length &&
  //   type === EAffiliateOrderType.NORMAL &&
  //   dayjs(workingAt).isAfter(dayjs("2026-01-19"), "day")
  // ) {
  //   days = DEADLINE_DAYS_IN_APP
  // }

  const deadline = dayjs(expiresAt).format("DD/MM/YYYY")

  return (
    <div className="bg-orange-200/30 mt-1 border border-orange-500 text-orange-500 rounded-md p-2 w-full text-xs font-medium">
      <p>Thời hạn : {deadline}</p>
    </div>
  )
}

import { useCampaignData } from "@/features/jobs/manage/hooks/use-campaign-data"
import { REASON_REJECT_ACCEPTANCE_MESSAGE } from "@/modules/helpers/options"

// ============================================================================
// Constants
// ============================================================================
// ============================================================================
// Main Components
// ============================================================================

interface SubmissionItemProps {
  submission?: SubmissionCampaign
}

const SubmissionItem: React.FC<SubmissionItemProps> = ({ submission }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isOrderSheetOpen, setIsOrderSheetOpen] = useState(false)
  const router = useRouter()
  const { copied, copy } = useCopyToClipboard(2000)
  const { setId } = useShowSubmission()

  const { campaign, campaignType, folderAssets } = useCampaignData(submission)

  const status = submission?.status ?? ESubmitStatus.APPLIED
  const isOverdue = status === ESubmitStatus.OVERDUE
  const hasProtest = !!submission?.protestOverdues?.reason
  const affiliateCampaign = campaign as AffiliateCampaign

  const handleNavigateToDetail = () => {
    sendHaptic()
    setId(submission?.id || "", submission?.conversationCode)
  }

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    sendHaptic()

    if (!submission?.conversationCode) {
      console.warn("Missing conversation code")
      return
    }

    router.navigate({
      to: "/manage/chat/$code",
      params: { code: submission.conversationCode },
      search: {
        shopName: campaign?.shopName,
        otherUserId: campaign?.createdBy,
        submissionId: submission?.id
      }
    } as any)
  }

  const handleComplaintClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(true)
  }

  const handleCopyTracking = () => {
    const trackingNumber = submission?.requestProductSamples?.trackingNumber
    if (trackingNumber) {
      copy(trackingNumber)
    }
  }

  const handleOrderClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    sendHaptic()
    setIsOrderSheetOpen(true)
  }

  return (
    <>
      <div
        onClick={handleNavigateToDetail}
        className="flex flex-col w-full cursor-pointer rounded-lg transition-all overflow-hidden relative h-fit"
      >
        <div className="flex gap-1 items-start">
          <Thumbnail
            campaign={campaign}
            folderAssets={folderAssets}
            shopName={submission?.affCampaigns?.shopName}
            onChatClick={handleChatClick}
          />

          <div className="flex-1 w-full px-2 break-words whitespace-pre-wrap flex overflow-hidden flex-col">
            <StatusBadges isOverdue={isOverdue} hasProtest={hasProtest} />

            {/* Title */}
            <div className="flex flex-col gap-1 mb-2">
              <h2 className="text-sm line-clamp-2 whitespace-pre-wrap font-semibold">
                {campaign?.title}
              </h2>
            </div>

            <Tags isSelfFaceRequired={affiliateCampaign?.isSelfFaceRequired} />
            <CashAmount amount={affiliateCampaign?.cashAmount} />

            <div className="flex-col flex gap-1">
              <StatusSection
                submission={submission}
                skuTitle={submission?.requestProductSamples?.skus?.title}
                onComplaintClick={handleComplaintClick}
                onOrderClick={handleOrderClick}
              />

              <DeliveryInfo submission={submission} copied={copied} onCopy={handleCopyTracking} />

              <DraftDueDates campaign={campaign as Campaign} campaignType={campaignType} />

              {submission?.status === ESubmitStatus.REJECTED_ACCEPTANCE && (
                <RejectReason
                  reason={
                    submission?.metadataContent?.reasonReject ||
                    (submission?.reasonRejectAcceptance
                      ? REASON_REJECT_ACCEPTANCE_MESSAGE[
                          submission.reasonRejectAcceptance as EReasonRejectAcceptance
                        ]
                      : "")
                  }
                />
              )}

              <DeadlineInfo
                workingAt={submission?.workingAt}
                status={submission?.status}
                expiresAt={submission?.expiresAt}
                order={submission?.ordersTap}
                type={submission?.affCampaigns?.type}
              />
            </div>
          </div>
        </div>
      </div>

      {isOverdue && submission?.id && (
        <ComplaintModal kolSubmissionId={submission.id} isOpen={isOpen} setIsOpen={setIsOpen} />
      )}

      <OrderTrackingSheet
        open={isOrderSheetOpen}
        onClose={() => setIsOrderSheetOpen(false)}
        order={submission?.ordersTap}
      />
    </>
  )
}

const SubmissionItemSkeleton: React.FC = () => (
  <div className="flex w-full cursor-pointer bg-white transition-all gap-1 overflow-hidden relative items-start h-fit">
    <Skeleton className="w-40 h-40 rounded-2xl flex-shrink-0" />

    <div className="flex-1 w-full px-2 flex overflow-hidden flex-col">
      <Skeleton className="w-3/4 h-[20px] mb-2" />

      <div className="flex gap-1 mb-2">
        <Skeleton className="h-5 w-[80px] rounded-full" />
        <Skeleton className="h-5 w-[70px] rounded-full" />
      </div>

      <div className="flex-col flex gap-1">
        <Skeleton className="w-full h-[36px] rounded-md" />
        <Skeleton className="w-full h-[40px] rounded-md mt-1" />
      </div>
    </div>
  </div>
)

export { SubmissionItem, SubmissionItemSkeleton }
